import { useState, useCallback } from 'react';
import { CaseStudyVersion, CaseStudyVersionType } from '@/types/case-study';
import { CaseStudyVersionService } from '@/services/supabase/caseStudyVersions';
import type { CaseStudyItem } from '@/types/case-study';

interface UseVersionHistoryOptions {
  caseStudyId?: string;
  autoLoad?: boolean;
}

interface UseVersionHistoryReturn {
  versions: CaseStudyVersion[];
  isLoading: boolean;
  isSaving: boolean;
  error: Error | null;
  loadVersions: () => Promise<void>;
  createVersion: (caseStudy: CaseStudyItem, label?: string, versionType?: CaseStudyVersionType) => Promise<CaseStudyVersion>;
  deleteVersion: (versionId: string) => Promise<void>;
  getVersion: (versionId: string) => Promise<CaseStudyVersion>;
  refreshVersions: () => Promise<void>;
}

/**
 * Hook for managing case study version history
 * Handles loading, creating, and deleting versions
 */
export function useVersionHistory(
  options: UseVersionHistoryOptions = {}
): UseVersionHistoryReturn {
  const { caseStudyId, autoLoad = false } = options;

  const [versions, setVersions] = useState<CaseStudyVersion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Load all versions for the case study
   */
  const loadVersions = useCallback(async () => {
    if (!caseStudyId) {
      setVersions([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const loadedVersions = await CaseStudyVersionService.getVersions(caseStudyId);
      setVersions(loadedVersions);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load versions');
      setError(error);
      console.error('[useVersionHistory] Error loading versions:', err);
    } finally {
      setIsLoading(false);
    }
  }, [caseStudyId]);

  /**
   * Create a new version
   */
  const createVersion = useCallback(
    async (
      caseStudy: CaseStudyItem,
      label?: string,
      versionType: CaseStudyVersionType = 'manual'
    ): Promise<CaseStudyVersion> => {
      setIsSaving(true);
      setError(null);

      try {
        const newVersion = await CaseStudyVersionService.createVersion(
          caseStudy,
          label,
          versionType
        );

        // Add to local state (prepend since we order by version_number DESC)
        setVersions((prev) => [newVersion, ...prev]);

        return newVersion;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to create version');
        setError(error);
        console.error('[useVersionHistory] Error creating version:', err);
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    []
  );

  /**
   * Delete a version
   */
  const deleteVersion = useCallback(async (versionId: string): Promise<void> => {
    setError(null);

    try {
      await CaseStudyVersionService.deleteVersion(versionId);

      // Remove from local state
      setVersions((prev) => prev.filter((v) => v.id !== versionId));
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to delete version');
      setError(error);
      console.error('[useVersionHistory] Error deleting version:', err);
      throw error;
    }
  }, []);

  /**
   * Get a specific version by ID
   */
  const getVersion = useCallback(async (versionId: string): Promise<CaseStudyVersion> => {
    setError(null);

    try {
      return await CaseStudyVersionService.getVersion(versionId);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to get version');
      setError(error);
      console.error('[useVersionHistory] Error getting version:', err);
      throw error;
    }
  }, []);

  /**
   * Refresh versions list
   */
  const refreshVersions = useCallback(async () => {
    await loadVersions();
  }, [loadVersions]);

  return {
    versions,
    isLoading,
    isSaving,
    error,
    loadVersions,
    createVersion,
    deleteVersion,
    getVersion,
    refreshVersions,
  };
}
